import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { fetchTenantBillingSummary, formatMoney } from '../../services/billingApi';
import { formatAppDate } from '../../services/appConfigRuntime';
import { useAppConfig } from '../../context/AppConfigContext';

const getTenantId = () => {
  const tid = localStorage.getItem('tenantId');
  if (!tid || tid === 'ALL' || tid === 'null' || tid === 'undefined') return null;
  return tid;
};

/** Spec §8.6 Tenant Billing & Subscription — read-only */
const TenantBilling = () => {
  const { currency: appCurrency } = useAppConfig();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const load = useCallback(async () => {
    const tenantId = getTenantId();
    if (!tenantId) {
      setError('No tenant selected.');
      setLoading(false);
      return;
    }
    setLoading(true);
    setError('');
    try {
      setData(await fetchTenantBillingSummary(tenantId));
    } catch (e) {
      setError(e.message || 'Failed to load billing summary');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const account = data?.billingAccount;
  const subscription = data?.subscription || {};
  const charges = data?.charges || [];
  const invoices = data?.invoices || [];
  const payments = data?.payments || [];
  const usage = data?.usage || [];
  const currency = account?.currency || subscription.currency || appCurrency || 'USD';

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h5" sx={{ mb: 2 }}>
        Billing &amp; Subscription
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
          <CircularProgress />
        </Box>
      ) : (
        <>
          <Grid container spacing={2} sx={{ mb: 3 }}>
            <Grid item xs={12} md={6}>
              <Card variant="outlined">
                <CardContent>
                  <Typography variant="subtitle2" color="text.secondary">
                    Plan / licensed products
                  </Typography>
                  {subscription.subscriptionId ? (
                    <>
                      <Typography variant="h6">
                        {subscription.planName || subscription.planCode || 'Subscription'}
                      </Typography>
                      <Typography variant="body2">Status: {subscription.subscriptionStatus}</Typography>
                      <Typography variant="body2">
                        Term: {formatAppDate(subscription.startDate)} → {formatAppDate(subscription.endDate)}
                      </Typography>
                      <Typography variant="body2">
                        Estimated recurring: {formatMoney(data?.estimatedRecurring, currency)}
                      </Typography>
                    </>
                  ) : (
                    <Typography color="text.secondary">No subscription.</Typography>
                  )}
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} md={6}>
              <Card variant="outlined">
                <CardContent>
                  <Typography variant="subtitle2" color="text.secondary">
                    Billing profile
                  </Typography>
                  {account ? (
                    <>
                      <Typography variant="h6">{account.legalCompanyName}</Typography>
                      <Typography variant="body2">{account.billingEmail || '—'}</Typography>
                      <Typography variant="body2">
                        {account.currency} · {account.paymentTerms}
                      </Typography>
                    </>
                  ) : (
                    <Typography color="text.secondary">No billing account.</Typography>
                  )}
                </CardContent>
              </Card>
            </Grid>
          </Grid>

          <Typography variant="h6" sx={{ mb: 1 }}>
            Quantities / charges
          </Typography>
          <Table size="small" sx={{ mb: 3 }}>
            <TableHead>
              <TableRow>
                <TableCell>Product</TableCell>
                <TableCell>Charge</TableCell>
                <TableCell align="right">Qty</TableCell>
                <TableCell align="right">Rate</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {charges.filter((c) => c.active !== false).map((c) => (
                <TableRow key={c.subscriptionChargeId}>
                  <TableCell>{c.productCode}</TableCell>
                  <TableCell>{c.chargeName || c.chargeCode}</TableCell>
                  <TableCell align="right">{c.contractedQuantity}</TableCell>
                  <TableCell align="right">{formatMoney(c.contractedUnitPrice, currency)}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>

          <Typography variant="h6" sx={{ mb: 1 }}>
            Usage
          </Typography>
          <Table size="small" sx={{ mb: 3 }}>
            <TableHead>
              <TableRow>
                <TableCell>Type</TableCell>
                <TableCell align="right">Included</TableCell>
                <TableCell align="right">Used</TableCell>
                <TableCell align="right">Overage</TableCell>
                <TableCell>Status</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {usage.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={5}>
                    <Typography color="text.secondary">No usage rows.</Typography>
                  </TableCell>
                </TableRow>
              ) : (
                usage.map((u) => (
                  <TableRow key={u.usageBillingId}>
                    <TableCell>{u.usageType}</TableCell>
                    <TableCell align="right">{u.includedQuantity}</TableCell>
                    <TableCell align="right">{u.actualQuantity}</TableCell>
                    <TableCell align="right">{u.billableQuantity}</TableCell>
                    <TableCell>
                      <Chip size="small" label={u.billingStatus} />
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>

          <Typography variant="h6" sx={{ mb: 1 }}>
            Invoices / balances
          </Typography>
          <Table size="small" sx={{ mb: 3 }}>
            <TableHead>
              <TableRow>
                <TableCell>Invoice #</TableCell>
                <TableCell>Date</TableCell>
                <TableCell>Due</TableCell>
                <TableCell align="right">Total</TableCell>
                <TableCell align="right">Balance</TableCell>
                <TableCell>Status</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {invoices.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6}>
                    <Typography color="text.secondary">No invoices.</Typography>
                  </TableCell>
                </TableRow>
              ) : (
                invoices.map((i) => (
                  <TableRow key={i.invoiceId}>
                    <TableCell>{i.invoiceNumber}</TableCell>
                    <TableCell>{String(i.invoiceDate || '').slice(0, 10)}</TableCell>
                    <TableCell>{String(i.dueDate || '').slice(0, 10)}</TableCell>
                    <TableCell align="right">{formatMoney(i.totalAmount, i.currency)}</TableCell>
                    <TableCell align="right">{formatMoney(i.balanceDue, i.currency)}</TableCell>
                    <TableCell>
                      <Chip size="small" label={i.invoiceStatus} />
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>

          <Typography variant="h6" sx={{ mb: 1 }}>
            Payment status
          </Typography>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Date</TableCell>
                <TableCell>Invoice</TableCell>
                <TableCell>Method</TableCell>
                <TableCell align="right">Amount</TableCell>
                <TableCell>Status</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {payments.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={5}>
                    <Typography color="text.secondary">No payments.</Typography>
                  </TableCell>
                </TableRow>
              ) : (
                payments.map((p) => (
                  <TableRow key={p.paymentId}>
                    <TableCell>{String(p.paymentDate || '').slice(0, 10)}</TableCell>
                    <TableCell>{p.invoiceNumber || '—'}</TableCell>
                    <TableCell>{p.paymentMethod || '—'}</TableCell>
                    <TableCell align="right">{formatMoney(p.amount, p.currency)}</TableCell>
                    <TableCell>
                      <Chip size="small" label={p.paymentStatus} />
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </>
      )}
    </Box>
  );
};

export default TenantBilling;
